import { useCallback } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { ChevronsUpDown, LogOut, Settings, ShieldCheck } from "lucide-react";

import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";

import { useAuth } from "@/hooks/useAuth";

export function NavUser() {
  const { t } = useTranslation();
  const { logout, usuario } = useAuth();
  const { isMobile } = useSidebar();

  const iniciais = useCallback(() => {
    const nome = usuario?.nome?.trim();
    if (!nome) return "?";
    return nome
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join("");
  }, [usuario?.nome]);

  const fallback = (
    <AvatarFallback className="rounded-lg bg-primary/10 text-primary font-medium">
      {iniciais()}
    </AvatarFallback>
  );

  const isAdmin = Boolean((usuario as any)?.is_admin);

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="cursor-pointer data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">{fallback}</Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                <span className="truncate font-medium">
                  {usuario?.nome || t("user.noName")}
                </span>
                <span className="text-muted-foreground truncate text-xs">
                  {usuario?.login || "—"}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto h-4 w-4 text-muted-foreground" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-2 py-2 text-left text-sm">
                <Avatar className="h-9 w-9 rounded-lg">{fallback}</Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight min-w-0">
                  <span className="truncate font-medium">
                    {usuario?.nome || t("user.noName")}
                  </span>
                  <span className="text-muted-foreground truncate text-xs">
                    {usuario?.login || "—"}
                  </span>
                </div>
              </div>
              {isAdmin && (
                <div className="px-2 pb-2">
                  <Badge
                    variant="outline"
                    className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20 gap-1 text-[10px]"
                  >
                    <ShieldCheck className="h-3 w-3" />
                    {t("user.admin")}
                  </Badge>
                </div>
              )}
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuItem asChild className="cursor-pointer select-none">
              <Link to="/preferencias">
                <Settings className="h-4 w-4" />
                {t("user.preferences")}
              </Link>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              className="cursor-pointer select-none text-red-600 focus:text-red-700 focus:bg-red-500/10"
              onClick={logout}
            >
              <LogOut className="h-4 w-4" />
              {t("user.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
